import React, { useEffect, useRef, useState } from 'react';
import { ActionButton } from '@adobe/react-spectrum';
import More from '@spectrum-icons/workflow/More';
import './BreadCrumbActionBar.css';

/**
 * Wraps a row of breadcrumb action buttons. On narrow viewports, any
 * children beyond `maxVisibleMobile` are tucked behind a "…" overflow
 * trigger that opens a popover with the remaining actions stacked.
 *
 * `secondaryPrimary` is an optional action that stays visible on every
 * viewport size. On desktop it renders just before the primary (last
 * child). On mobile it joins the visible row between the visible prefix
 * and the overflow menu. Use this for actions like Refresh that need to
 * sit next to the primary CTA regardless of width.
 */
export function BreadCrumbActionBar({ children, maxVisibleMobile = 2, secondaryPrimary = null }) {
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [isNarrow, setIsNarrow] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(max-width: 720px)').matches;
    });
    const wrapRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mql = window.matchMedia('(max-width: 720px)');
        const onChange = (e) => setIsNarrow(e.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (!overflowOpen) return undefined;
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOverflowOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOverflowOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [overflowOpen]);

    const items = React.Children.toArray(children).filter(Boolean);

    if (!isNarrow || items.length <= maxVisibleMobile + 1) {
        // Desktop (or very few buttons): render everything inline. When a
        // secondaryPrimary is provided, slot it just before the primary
        // (last) child so it sits immediately to the left of the CTA.
        if (secondaryPrimary) {
            const head = items.slice(0, items.length - 1);
            const primary = items[items.length - 1];
            return (
                <div className="bcActionBar">
                    {head}
                    {secondaryPrimary}
                    {primary}
                </div>
            );
        }
        return <div className="bcActionBar">{items}</div>;
    }

    // Narrow viewport + too many buttons. Convention: the LAST child is
    // the primary CTA (usually the "+" import button) and must always
    // stay visible. Middle buttons collapse into the overflow menu, and
    // the few that stay in the top bar get their labels hidden via
    // `data-compact` so icons alone remain. A secondaryPrimary, if
    // provided, joins the visible row right before the overflow trigger.
    const primary = items[items.length - 1];
    const rest = items.slice(0, items.length - 1);
    const visible = rest.slice(0, maxVisibleMobile);
    const overflow = rest.slice(maxVisibleMobile);

    return (
        <div className="bcActionBar" data-compact="true" ref={wrapRef}>
            {visible}
            {secondaryPrimary}
            {overflow.length > 0 && (
                <div className="bcOverflowWrap">
                    <ActionButton
                        aria-label="More actions"
                        aria-expanded={overflowOpen}
                        data-role="icon-only"
                        onPress={() => setOverflowOpen((v) => !v)}
                    >
                        <More />
                    </ActionButton>
                    {overflowOpen && (
                        <div
                            className="bcOverflowMenu"
                            role="menu"
                            onClick={() => setOverflowOpen(false)}
                        >
                            {overflow.map((item, idx) => (
                                <div key={idx} className="bcOverflowItem" role="menuitem">
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {primary}
        </div>
    );
}
